/**
 * CEL expression parsing and type resolution utilities.
 * Used by autocomplete, hover, and signature help providers.
 */

import type { CELSchema } from 'components/core/editor/CELContext'

// ============================================================================
// Expression Context Parsing
// ============================================================================

export interface ExpressionContext {
    /** Path segments before the cursor (e.g., ["subject", "manager"]) */
    basePath: string[]
    /** Partial text being typed at cursor */
    partial: string
    /** Column where the partial text starts (1-indexed) */
    startColumn: number
}

export interface FunctionContext {
    /** Name of the function being called, if any */
    functionName: string | undefined
    /** 0-indexed parameter position */
    activeParameter: number
}

/**
 * Parse the expression context at cursor position.
 *
 * @example
 * // For "subject.manager.dis|" at column 20:
 * // Returns { basePath: ["subject", "manager"], partial: "dis", startColumn: 17 }
 *
 * @example
 * // For "sub|" at column 4:
 * // Returns { basePath: [], partial: "sub", startColumn: 1 }
 */
export const parseExpressionContext = (line: string, column: number): ExpressionContext => {
    // Find the start of the current expression (identifier chars and dots)
    let start = column - 1
    while (start > 0 && /[\w.]/.test(line[start - 1])) {
        start--
    }

    const fullExpr = line.slice(start, column - 1)
    const parts = fullExpr.split('.')

    if (parts.length === 0) {
        return { basePath: [], partial: '', startColumn: column }
    }

    // The last part is what's being typed (partial), everything before is the base path
    const partial = parts[parts.length - 1]
    const basePath = parts.slice(0, -1).filter((p) => p.length > 0)

    // startColumn is where the partial starts
    const partialStart = column - partial.length

    return { basePath, partial, startColumn: partialStart }
}

/**
 * Parse function context to find function name and active parameter index.
 *
 * @example
 * // For "c1.user.v1.HasEntitlement(subject, |":
 * // Returns { functionName: "c1.user.v1.HasEntitlement", activeParameter: 1 }
 */
export const parseFunctionContext = (lineUpToPosition: string): FunctionContext => {
    // Find the last unmatched open parenthesis
    let parenDepth = 0
    let funcEndIndex = -1

    for (let i = lineUpToPosition.length - 1; i >= 0; i--) {
        const char = lineUpToPosition[i]
        if (char === ')') {
            parenDepth++
        } else if (char === '(') {
            if (parenDepth === 0) {
                funcEndIndex = i
                break
            }
            parenDepth--
        }
    }

    if (funcEndIndex === -1) {
        return { functionName: undefined, activeParameter: 0 }
    }

    // Extract function name (walk backwards from '(' to find identifier)
    let funcStartIndex = funcEndIndex - 1
    while (funcStartIndex >= 0 && /[\w.]/.test(lineUpToPosition[funcStartIndex])) {
        funcStartIndex--
    }
    funcStartIndex++

    const functionName = lineUpToPosition.slice(funcStartIndex, funcEndIndex)
    if (!functionName) {
        return { functionName: undefined, activeParameter: 0 }
    }

    // Count commas at the current paren depth to determine active parameter
    const argsSection = lineUpToPosition.slice(funcEndIndex + 1)
    let activeParameter = 0
    parenDepth = 0

    for (const char of argsSection) {
        if (char === '(') {
            parenDepth++
        } else if (char === ')') {
            parenDepth--
        } else if (char === ',' && parenDepth === 0) {
            activeParameter++
        }
    }

    return { functionName, activeParameter }
}

// ============================================================================
// Type Resolution
// ============================================================================

/**
 * Resolve a path to its type using the schema.
 *
 * @example
 * // For path ["subject", "manager", "email"] with appropriate schema:
 * // Returns "string"
 */
export const resolveTypeForPath = (path: string[], schema: CELSchema): string | undefined => {
    if (path.length === 0) return undefined

    // Find the root variable
    const rootVar = schema.variables.find((v) => v.name === path[0])
    if (!rootVar) return undefined

    let currentType = rootVar.typeName

    // Resolve each subsequent field
    for (let i = 1; i < path.length; i++) {
        const fieldName = path[i]
        const typeToFind = currentType
        const typeDef = schema.types.find((t) => t.name === typeToFind)
        if (!typeDef) return undefined

        const field = typeDef.fields.find((f) => f.name === fieldName)
        if (!field) return undefined

        currentType = field.typeName
    }

    return currentType
}

/** Check if a type is a list type (e.g., "list(string)", "list(c1.user.v1)"). */
export const isListType = (typeName: string): boolean => typeName.startsWith('list(')

/** Check if a string matches a filter (case-insensitive prefix match). */
export const matchesFilter = (name: string, filter: string): boolean => {
    if (!filter) return true
    return name.toLowerCase().startsWith(filter.toLowerCase())
}
